import { useState } from 'react';
import { Button, Col, Modal, Row } from 'antd';
import { useNotify, useRefresh } from 'react-admin';
import { makeStyles } from '@mui/styles';
import { clsx } from 'clsx';

import { Credit, CreditStatus } from '@types';
import { BlueButton, BlueOutlineButton, InitialCard } from '@components';
import { creditHttp } from '@network';
import { rbac, showAxiosError } from '@utils';
import { CreditReportWidget } from '@pages/credit/CreditReportWidget';

export const CreditFileScreeningWidget = ({ credit }: {credit: Credit}) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const [isAnalysisModalOpened, setAnalysisModalOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const cls = useStyles();

  const hasSalesTeam = rbac.hasAreaSales();
  const hasCreditKycTeam = rbac.hasAreaKyc() || rbac.hasAreaCredit();

  const handleStatusChange = async (newStatus: CreditStatus) => {
    if (loading) return;
    try {
      setLoading(true);
      await creditHttp.updateStatusProgress(credit.id, newStatus);
      notify('Credit Screening Process is updated');
      refresh();
    } catch (err: any) {
      showAxiosError(err);
    } finally {
      setLoading(false);
    }
  };

  const sendToAnalysis = async () => {
    try {
      setLoading(true);
      await creditHttp.createCreditRisk(credit.id);
      notify('Credit Risk for Analysis is created');
      setAnalysisModalOpened(false);
      refresh();
    } catch (e: any) {
      showAxiosError(e);
    } finally {
      setLoading(false);
    }
  };

  const isSalesCompleted = ![
    CreditStatus.MODERATION,
    CreditStatus.PRE_SCREENING,
    CreditStatus.DOC_COLLECTION,
    CreditStatus.DRAFT,
    CreditStatus.DENIED,
  ].includes(credit.status);

  const isCreditKycCompleted = ![
    CreditStatus.MODERATION,
    CreditStatus.PRE_SCREENING,
    CreditStatus.DOC_COLLECTION,
    CreditStatus.KYC_SCREENING,
    CreditStatus.POST_SCREENING,
    CreditStatus.COMPLETED_FILE,
    CreditStatus.DRAFT,
    CreditStatus.DENIED,
  ].includes(credit.status);

  const showCreditKycControls = [
    CreditStatus.MODERATION,
    CreditStatus.PRE_SCREENING,
    CreditStatus.DOC_COLLECTION,
    CreditStatus.KYC_SCREENING,
    CreditStatus.DRAFT,
    CreditStatus.DENIED,
  ].includes(credit.status);

  const onAnalysisCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAnalysisModalOpened(false);
  };

  return (
    <InitialCard
      left="Credit Screening Process"
      autoHeight
      extraPadding
      minHeight={250}
      contentClass={cls.box}
    >
      <Row gutter={[40, 20]}>
        <Col span={12}>
          <div className={cls.col}>
            <div className={cls.title}>Sales Team</div>
            {/* Pre-Screening */}
            {!isSalesCompleted && (
              <BlueButton
                className={clsx(cls.btn, cls.btnPreScreening)}
                disabled={!hasSalesTeam
                  || credit.status !== CreditStatus.MODERATION
                  || loading}
                loading={loading}
                onClick={() => handleStatusChange(CreditStatus.PRE_SCREENING)}>
                Request Pre-Screening
                {credit.status === CreditStatus.MODERATION && <MarkIcon />}
              </BlueButton>
            )}

            {/* KYC Screening */}
            {!isSalesCompleted && (
              <BlueButton
                className={clsx(cls.btn, cls.btnKycScreening)}
                disabled={!hasSalesTeam
                  || credit.status !== CreditStatus.DOC_COLLECTION
                  || loading}
                loading={loading}
                onClick={() => handleStatusChange(CreditStatus.KYC_SCREENING)}
              >
                Request KYC Screening
                {credit.status === CreditStatus.DOC_COLLECTION && <MarkIcon />}
              </BlueButton>
            )}

            {isSalesCompleted && (
              <div className={cls.done}>
                <svg width="50"
                  height="50"
                  viewBox="0 0 50 50"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd"
                    clipRule="evenodd"
                    d="M25 0C38.8071 0 50 11.1929 50 25C50 38.8071 38.8071 50 25 50C11.1929 50 0 38.8071 0 25C0 11.1929 11.1929 0 25 0ZM39.9668 17.6631C39.0522 16.6238 37.468 16.5229 36.4287 17.4375L19.4922 32.3438L13.4199 25.4434C12.5053 24.4041 10.9211 24.3031 9.88184 25.2178C8.84255 26.1325 8.74156 27.7166 9.65625 28.7559L17.3848 37.5371C17.4419 37.602 17.5024 37.6641 17.5645 37.7217C18.496 38.5855 19.9486 38.6201 20.9229 37.7627L39.7412 21.2012C40.7803 20.2865 40.8814 18.7023 39.9668 17.6631Z"
                    fill="#0C5541" />
                </svg>
                <div className={cls.doneText}>Done</div>
              </div>
            )}
          </div>
        </Col>
        <Col span={12} style={{ borderLeft: '4px solid var(--primary-color)' }}>
          <div className={cls.col}>
            <div className={cls.title}>Credit/KYC Team</div>
            {/* COMPLETED TO ANALYSIS */}
            {isCreditKycCompleted && (
              <div className={cls.done}>
                <svg width="50"
                  height="50"
                  viewBox="0 0 50 50"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd"
                    clipRule="evenodd"
                    d="M25 0C38.8071 0 50 11.1929 50 25C50 38.8071 38.8071 50 25 50C11.1929 50 0 38.8071 0 25C0 11.1929 11.1929 0 25 0ZM39.9668 17.6631C39.0522 16.6238 37.468 16.5229 36.4287 17.4375L19.4922 32.3438L13.4199 25.4434C12.5053 24.4041 10.9211 24.3031 9.88184 25.2178C8.84255 26.1325 8.74156 27.7166 9.65625 28.7559L17.3848 37.5371C17.4419 37.602 17.5024 37.6641 17.5645 37.7217C18.496 38.5855 19.9486 38.6201 20.9229 37.7627L39.7412 21.2012C40.7803 20.2865 40.8814 18.7023 39.9668 17.6631Z"
                    fill="#0C5541" />
                </svg>
                <div className={cls.doneText}>Done</div>
              </div>
            )}

            {/* Doc Collection */}
            {showCreditKycControls && (
              <BlueButton
                className={clsx(cls.btn, cls.btnDocCollection)}
                disabled={!hasCreditKycTeam
                  || credit.status !== CreditStatus.PRE_SCREENING
                  || loading}
                loading={loading}
                onClick={() => handleStatusChange(CreditStatus.DOC_COLLECTION)}
              >
                Request Doc Collection
                {credit.status === CreditStatus.PRE_SCREENING && <MarkIcon />}
              </BlueButton>
            )}

            {/* Post-Screening */}
            {showCreditKycControls && (
              <BlueButton
                className={clsx(cls.btn, cls.btnPostScreening)}
                disabled={!hasCreditKycTeam
                  || credit.status !== CreditStatus.KYC_SCREENING
                  || loading}
                loading={loading}
                onClick={() => handleStatusChange(CreditStatus.POST_SCREENING)}
              >
                In Post Screening
                {credit.status === CreditStatus.KYC_SCREENING && <MarkIcon />}
              </BlueButton>
            )}

            {/* Step Back */}
            {showCreditKycControls && (
              <BlueOutlineButton
                className={clsx(cls.btn, cls.btnBack)}
                disabled={!hasCreditKycTeam
                  || credit.status !== CreditStatus.KYC_SCREENING
                  || loading}
                onClick={() => handleStatusChange(CreditStatus.DOC_COLLECTION)}
              >
                Step Back
              </BlueOutlineButton>
            )}

            {/* Completed File */}
            {credit.status === CreditStatus.POST_SCREENING && (
              <BlueButton
                className={clsx(cls.btn, cls.btnCompletedFile)}
                disabled={loading}
                loading={loading}
                onClick={() => handleStatusChange(CreditStatus.COMPLETED_FILE)}
              >
                Completed File
                <MarkIcon />
              </BlueButton>
            )}

            {/* Send to Analysis */}
            {credit.status === CreditStatus.COMPLETED_FILE && (
              <div className={cls.send}>
                <BlueButton
                  className={clsx(cls.btn, cls.btnCompletedFile)}
                  disabled={true}
                >
                  Completed File
                </BlueButton>
                <BlueButton
                  className={clsx(cls.btn, cls.btnSendToAnalysis)}
                  disabled={loading}
                  loading={loading}
                  onClick={() => setAnalysisModalOpened(true)}
                >
                  <span>Send to analysis</span>
                  <svg width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.499245 5.06437C0.185327 5.06437 -0.0359296 4.81312 0.0151294 4.5075C0.0151294 4.5075 -0.020801 4.6875 0.0151294 4.50375C0.540848 1.935 2.83094 -3.68009e-07 5.57678 -2.47984e-07C8.71029 -1.11014e-07 11.25 2.51813 11.25 5.625C11.25 8.73187 8.71029 11.25 5.57678 11.25C2.83472 11.25 0.546521 9.32062 0.0189116 6.75562C-0.0189099 6.57 0.0170204 6.7425 0.0170204 6.7425C-0.0302564 6.435 0.187217 6.1875 0.501135 6.1875L5.86422 6.1875C6.07413 6.1875 6.12141 6.30562 5.9739 6.45375L4.94138 7.47187C4.4062 8.00062 5.20991 8.79375 5.74319 8.265L8.05787 6.00937C8.28101 5.79187 8.28101 5.44125 8.05598 5.22375L5.74508 2.98875C5.20991 2.46 4.40809 3.25312 4.94327 3.78187L5.9758 4.8C6.12519 4.94625 6.07413 5.06625 5.86611 5.06625L0.503026 5.06625L0.499245 5.06437Z"
                      fill="white" />
                  </svg>
                  <MarkIcon />
                </BlueButton>
              </div>
            )}
          </div>
        </Col>
      </Row>

      <Modal
        closable={false}
        title={false}
        open={isAnalysisModalOpened}
        onOk={sendToAnalysis}
        onCancel={onAnalysisCancel}
        destroyOnClose
        width={'1000px'}
        footer={[
          <Button key="back" onClick={onAnalysisCancel} className={cls.modalCancelBtn}>
            Cancel
          </Button>,
          <Button key="submit"
            type="primary"
            loading={false}
            onClick={sendToAnalysis}
            className={cls.modalSubmitBtn}>
            Send
          </Button>,
        ]}
      >
        <div onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}>
          <div className={cls.analysisModalTitle}>Send To Analysis</div>
          <CreditReportWidget credit={credit} />
        </div>
      </Modal>
    </InitialCard>
  );
};

const useStyles = makeStyles({
  box: {
    paddingTop: 10,
  },
  col: {
    width: 182,
    margin: '0 auto',
  },
  title: {
    fontSize: 16,
    lineHeight: 1,
    color: 'var(--text-color)',
    textAlign: 'center',
  },
  done: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 30,
  },
  doneText: {
    marginTop: 5,
  },
  btn: {
    paddingLeft: 2,
    paddingRight: 2,
    fontSize: 16,
    width: 182,
    maxWidth: 182,
    height: 40,
    maxHeight: 40,
    marginTop: 12,
  },
  btnPreScreening: {
    backgroundColor: '#1D1DB2',
    '&:disabled': {
      backgroundColor: '#1D1DB2 !important',
      color: '#FFF',
      opacity: 0.5,
    },
  },
  btnKycScreening: {
    backgroundColor: '#007BFF',
    '&:disabled': {
      backgroundColor: '#007BFF !important',
      color: '#FFF',
      opacity: 0.5,
    },
  },
  btnDocCollection: {
    backgroundColor: '#3B3BB8',
    '&:disabled': {
      backgroundColor: '#3B3BB8 !important',
      color: '#FFF',
      opacity: 0.5,
    },
  },
  btnPostScreening: {
    backgroundColor: '#00BFFF',
    '&:disabled': {
      backgroundColor: '#00BFFF !important',
      color: '#FFF',
      opacity: 0.5,
    },
  },
  btnCompletedFile: {
    marginTop: 35,
    backgroundColor: '#20C997',
    '&:disabled': {
      backgroundColor: '#20C997 !important',
      color: '#FFF',
      opacity: 0.5,
    },
    '&:hover': {
      backgroundColor: '#1AA77D',
    },
  },
  btnBack: {
    textTransform: 'none',
    borderRadius: 8,
    borderWidth: 2,
    '&:hover': {
      borderWidth: 2,
    },
  },
  send: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnSendToAnalysis: {
    marginTop: 10,
    backgroundColor: '#20B2AA',
    '&:disabled': {
      backgroundColor: '#20B2AA !important',
      color: '#FFF',
      opacity: 0.5,
    },
    '&:hover': {
      backgroundColor: '#147C76',
    },
    borderRadius: 30,
    height: 25,
    width: 145,
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: 10,
    fontSize: 15,
  },
  markIcon: {
    position: 'absolute',
    top: 10,
    left: -30,
  },
  analysisModalTitle: {
    fontFamily: 'var(--font-primary)',
    fontWeight: 700,
    fontSize: 25,
    lineHeight: 1,
    color: 'var(--text-color)',
  },
  modalCancelBtn: {
    width: 200,
    height: 38,
    borderRadius: 8,
    border: '1px solid #D72B2C',
    color: '#D72B2C',
    '&:hover': {
      opacity: 0.8,
      border: '1px solid #D72B2C',
      color: '#D72B2C',
    },
    '&:visited': {
      border: '1px solid #D72B2C',
      color: '#D72B2C',
    },
    '&:active': {
      border: '1px solid #D72B2C',
      color: '#D72B2C',
    },
  },
  modalSubmitBtn: {
    width: 200,
    height: 38,
    borderRadius: 8,
    '&:hover': {
      opacity: 0.8,
      background: 'var(--primary-color)',
      border: '1px solid var(--primary-color)',
    },
    '&:visited': {
      background: 'var(--primary-color)',
      border: '1px solid var(--primary-color)',
    },
    '&:active': {
      background: 'var(--primary-color)',
      border: '1px solid var(--primary-color)',
    },
  },
});

const MarkIcon = () => {
  const cls = useStyles();

  return (
    <svg width="19" height="17" className={cls.markIcon}
      viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd"
        clipRule="evenodd"
        d="M7.21431 1.30768C8.23014 -0.435902 10.7697 -0.435886 11.7855 1.30768L18.6425 13.0769C19.6583 14.8205 18.3883 17 16.3567 17H2.64312C0.61146 17 -0.658303 14.8205 0.357528 13.0769L7.21431 1.30768ZM9.45351 12.9012C9.01436 12.9012 8.64833 13.0517 8.35556 13.353C8.06278 13.6432 7.91638 14.0116 7.91638 14.4579C7.9164 14.8819 8.0628 15.2447 8.35556 15.546C8.64833 15.8472 9.01437 15.9978 9.45351 15.9978C9.90391 15.9978 10.27 15.8472 10.5515 15.546C10.8442 15.2447 10.9906 14.8819 10.9906 14.4579C10.9906 14.0116 10.8442 13.6432 10.5515 13.353C10.27 13.0517 9.90392 12.9012 9.45351 12.9012ZM8.06844 3.66153L8.49061 11.4952H10.4332L10.8553 3.66153H8.06844Z"
        fill="#F38336" />
    </svg>
  );
};
